import { Injectable } from "@angular/core";
import { HttpParams } from "@angular/common/http";

import { Action, Selector, StateContext, State } from "@ngxs/store";
import { patch } from "@ngxs/store/operators";
import { tap, finalize, catchError } from "rxjs";

import { AutomationsService } from "../../services/automations/automations.service";
import { Automations } from "./automation.actions";
import { Automation } from "../../models/automation/automation.model";
import { PaginatedResult } from "../paginated_result.interface";
import { Filter } from "../../models/filter/filter.model";

export interface AutomationStateModel extends PaginatedResult<Automation> {
	selectedAutomation?: Automation;
}

@State<AutomationStateModel>({
	name: "automationstate",
	defaults: {
		items: [],
		filter: {
			page: 0,
			per_page: 0,
			q: "",
			total: 0,
			f: [],
		},
		loading: false,
	},
})
@Injectable()
export class AutomationState {
	constructor(private automationService: AutomationsService) {}

	@Selector()
	static getAutomations(state: AutomationStateModel) {
		return state.items;
	}

	@Selector()
	static getAutomation(state: AutomationStateModel) {
		return (id: number) => {
			return state.items.find((a: Automation) => {
				return a.id == id;
			});
		};
	}

	@Selector()
	static getSelectedAutomation(state: AutomationStateModel) {
		return state.selectedAutomation;
	}

	@Selector()
	static filter(state: AutomationStateModel): Filter {
		return state.filter;
	}

	@Selector()
	static loading(state: AutomationStateModel) {
		return state.loading;
	}

	@Action(Automations.SetFilter)
	setFilter(ctx: StateContext<AutomationStateModel>, action: Automations.SetFilter) {
		return ctx.setState(
			patch<AutomationStateModel>({
				filter: action.filter,
			})
		);
	}

	@Action(Automations.FetchAll)
	getAutomations(ctx: StateContext<AutomationStateModel>) {
		const state = ctx.getState();
		let filter: any = state.filter
			? Object.entries(state.filter).reduce((acc, [key, value]) => {
					acc[key] = value === null ? "" : value;
					return acc;
			  }, {} as any)
			: {};

		const options = {
			params: new HttpParams({
				fromObject: filter,
			}),
		};
		ctx.patchState({ loading: true });

		return this.automationService.fetchAutomations(options).pipe(
			tap((returnData: any) => {
				const state = ctx.getState();
				ctx.patchState({
					...state,
					items: returnData.data,
					filter: {
						...state.filter,
						page: returnData.current_page,
						per_page: returnData.per_page,
						total: returnData.total,
					},
				});
			}),
			finalize(() => {
				ctx.patchState({
					loading: false,
				});
			})
		);
	}

	@Action(Automations.FetchAutomation)
	getAutomation(ctx: StateContext<AutomationStateModel>, action: Automations.FetchAutomation) {
		const state = ctx.getState();
		ctx.patchState({ loading: true });

		let existing = state.items.find((a: Automation) => a.id === action.automationId);

		if (existing) {
			ctx.patchState({ selectedAutomation: existing });
			return;
		}

		return this.automationService.fetchAutomation(action.automationId).pipe(
			tap((returnData: any) => {
				const automation = returnData.data;
				ctx.patchState({
					selectedAutomation: automation,
				});
			}),
			finalize(() => {
				ctx.patchState({
					loading: false,
				});
			})
		);
	}

	@Action(Automations.Add)
	addAutomations(ctx: StateContext<AutomationStateModel>, action: Automations.Add) {
		ctx.patchState({ loading: true });

		return this.automationService.addAutomation(action.automation).pipe(
			tap((returnData: any) => {
				const newAutomation = returnData.data;
				ctx.patchState({
					selectedAutomation: newAutomation,
				});
			}),
			finalize(() => {
				ctx.patchState({
					loading: false,
				});
			})
		);
	}

	@Action(Automations.Update)
	updateAutomation(ctx: StateContext<AutomationStateModel>, action: Automations.Update) {
		ctx.patchState({
			loading: true,
		});
		return this.automationService.updateAutomation(action.automationId, action.automation).pipe(
			tap((returnData: any) => {
				const updated = returnData.data;
				const state = ctx.getState();

				ctx.patchState({
					items: state.items.map((p) => (p.id === action.automationId ? { ...p, ...updated } : p)),
					selectedAutomation:
						state.selectedAutomation?.id === action.automationId
							? { ...state.selectedAutomation, ...updated }
							: state.selectedAutomation,
				});
			}),
			finalize(() => {
				ctx.patchState({
					loading: false,
				});
			})
		);
	}

	@Action(Automations.Delete)
	deleteAutomation(ctx: StateContext<AutomationStateModel>, action: Automations.Delete) {
		ctx.patchState({
			loading: true,
		});
		return this.automationService.deleteAutomation(action.automationId).pipe(
			tap((returnData: any) => {
				const state = ctx.getState();
				ctx.patchState({
					items: state.items.filter((a: Automation) => a.id !== action.automationId),
					selectedAutomation: state.selectedAutomation?.id === action.automationId ? undefined : state.selectedAutomation,
				});
			}),
			finalize(() => {
				ctx.patchState({
					loading: false,
				});
			})
		);
	}

	@Action(Automations.ActivateAutomation)
	activateAutomation(ctx: StateContext<AutomationStateModel>, action: Automations.ActivateAutomation) {
		ctx.patchState({
			loading: true,
		});
		return this.automationService.activateDeactivateAutomation(action.automationId, true).pipe(
			tap((returnData: any) => {
				const state = ctx.getState();
				ctx.patchState({
					items: state.items.map((i) => (i.id == action.automationId ? { ...i, active: true } : i)),
					selectedAutomation:
						state.selectedAutomation?.id === action.automationId
							? { ...state.selectedAutomation, active: true }
							: state.selectedAutomation,
				});
			}),
			finalize(() => {
				ctx.patchState({
					loading: false,
				});
			})
		);
	}

	@Action(Automations.DeactivateAutomation)
	deactivateAutomation(ctx: StateContext<AutomationStateModel>, action: Automations.DeactivateAutomation) {
		ctx.patchState({
			loading: true,
		});
		return this.automationService.activateDeactivateAutomation(action.automationId, false).pipe(
			tap((returnData: any) => {
				const state = ctx.getState();
				ctx.patchState({
					items: state.items.map((i) => (i.id == action.automationId ? { ...i, active: false } : i)),
					selectedAutomation:
						state.selectedAutomation?.id === action.automationId
							? { ...state.selectedAutomation, active: false }
							: state.selectedAutomation,
				});
			}),
			finalize(() => {
				ctx.patchState({
					loading: false,
				});
			})
		);
	}
}
